// DESCRIPTION: Per-day availability editor with multiple time blocks per day. Used
// by both the event-creation wizard and the setup wizard via the `variant` prop.

import { __ } from '@wordpress/i18n';
import TimeInput from './TimeInput';

const WEEKDAYS = [
	{ key: 'mon', label: __( 'Monday', 'nemtly-booking' ) },
	{ key: 'tue', label: __( 'Tuesday', 'nemtly-booking' ) },
	{ key: 'wed', label: __( 'Wednesday', 'nemtly-booking' ) },
	{ key: 'thu', label: __( 'Thursday', 'nemtly-booking' ) },
	{ key: 'fri', label: __( 'Friday', 'nemtly-booking' ) },
	{ key: 'sat', label: __( 'Saturday', 'nemtly-booking' ) },
	{ key: 'sun', label: __( 'Sunday', 'nemtly-booking' ) },
];

const DEFAULT_BLOCK = { start: '09:00', end: '17:00' };

// Two style sets — keep markup identical, swap class names. The wizard variant
// hooks into class names defined in src/css/setup-wizard.css; the default
// variant uses Tailwind utilities inline.
const STYLE_VARIANTS = {
	default: {
		timeInputVariant: 'default',
		root: 'nemtly-availability-editor',
		day: 'nemtly-availability-day nb-flex nb-items-start nb-py-3 nb-border-b nb-border-neutral-200 last:nb-border-b-0 nb-transition-colors',
		dayOn: '',
		dayOff: 'hover:nb-bg-neutral-50',
		toggleCol:
			'nb-flex nb-items-center nb-w-36 nb-shrink-0 nb-pt-1.5 nb-pl-2',
		toggle: 'nb-flex nb-items-center nb-gap-2.5 nb-cursor-pointer nb-select-none',
		checkbox: 'nb-w-4 nb-h-4 nb-rounded',
		labelOn: 'nb-text-sm nb-font-medium nb-text-neutral-900',
		labelOff: 'nb-text-sm nb-font-medium nb-text-neutral-500',
		blocksCol: 'nb-flex-1',
		blocks: 'nb-space-y-2',
		block: 'nb-flex nb-items-center nb-gap-2',
		dash: 'nb-text-neutral-400 nb-text-sm',
		iconBtn:
			'nb-bg-transparent nb-border-0 nb-cursor-pointer nb-p-0.5 nb-transition-colors',
		removeBtn: 'nb-text-neutral-300 hover:nb-text-danger-500',
		addBtn: 'nb-text-neutral-400 hover:nb-text-success-500',
		offLabel: 'nb-text-sm nb-text-neutral-400 nb-m-0 nb-pt-1.5',
	},
	wizard: {
		timeInputVariant: 'wizard',
		root: 'nemtly-wizard-days',
		day: 'nemtly-wizard-day',
		dayOn: 'is-active',
		dayOff: 'is-off',
		toggleCol: 'nemtly-wizard-day__toggle-col',
		toggle: 'nemtly-wizard-day__toggle',
		checkbox: '',
		labelOn: 'nemtly-wizard-day__label',
		labelOff: 'nemtly-wizard-day__label',
		blocksCol: 'nemtly-wizard-day__blocks',
		blocks: 'nemtly-wizard-day__times-stack',
		block: 'nemtly-wizard-day__times',
		dash: 'nemtly-wizard-day__dash',
		iconBtn: 'nemtly-wizard-day__icon-btn',
		removeBtn: 'nemtly-wizard-day__icon-btn--remove',
		addBtn: 'nemtly-wizard-day__icon-btn--add',
		offLabel: 'nemtly-wizard-day__closed',
	},
};

/**
 * AvailabilityEditor — list of weekdays with toggleable active state and one
 * or more time blocks per day. Mutates a canonical availability object of the
 * form `{ mon: [{ start, end }, ...], tue: [...], ... }`. An empty array for
 * a day means that day is off.
 *
 * @since PBOOKING_SINCE
 *
 * @param {Object}   props
 * @param {Object}   props.availability Keyed by 3-letter day, each an array of { start, end }.
 * @param {Function} props.onChange     Called with the updated availability object.
 * @param {string}   [props.variant]    'default' (Tailwind, event wizard) | 'wizard' (setup wizard CSS).
 *
 * @return {JSX.Element}
 */
const AvailabilityEditor = ( {
	availability = {},
	onChange,
	variant = 'default',
} ) => {
	const s = STYLE_VARIANTS[ variant ] || STYLE_VARIANTS.default;

	const getBlocks = ( day ) => availability[ day ] || [];
	const isActive = ( day ) => getBlocks( day ).length > 0;

	const updateDay = ( day, blocks ) => {
		onChange( { ...availability, [ day ]: blocks } );
	};

	const toggleDay = ( day ) => {
		if ( isActive( day ) ) {
			updateDay( day, [] );
		} else {
			updateDay( day, [ { ...DEFAULT_BLOCK } ] );
		}
	};

	const updateBlock = ( day, index, field, value ) => {
		const blocks = [ ...getBlocks( day ) ];
		blocks[ index ] = { ...blocks[ index ], [ field ]: value };
		updateDay( day, blocks );
	};

	const addBlock = ( day ) => {
		const blocks = getBlocks( day );
		const lastEnd =
			blocks.length > 0 ? blocks[ blocks.length - 1 ].end : '09:00';
		updateDay( day, [ ...blocks, { start: lastEnd, end: '17:00' } ] );
	};

	const removeBlock = ( day, index ) => {
		const blocks = getBlocks( day ).filter( ( _, i ) => i !== index );
		updateDay( day, blocks );
	};

	return (
		<div className={ s.root }>
			{ WEEKDAYS.map( ( { key, label } ) => {
				const active = isActive( key );
				const blocks = getBlocks( key );

				return (
					<div
						key={ key }
						className={ `${ s.day } ${
							active ? s.dayOn : s.dayOff
						}` }
					>
						{ /* Day toggle */ }
						<div className={ s.toggleCol }>
							<label className={ s.toggle }>
								<input
									type="checkbox"
									checked={ active }
									onChange={ () => toggleDay( key ) }
									className={ s.checkbox }
								/>
								<span
									className={
										active ? s.labelOn : s.labelOff
									}
								>
									{ label }
								</span>
							</label>
						</div>

						{ /* Time blocks or off label */ }
						<div className={ s.blocksCol }>
							{ active ? (
								<div className={ s.blocks }>
									{ blocks.map( ( block, index ) => (
										<div
											key={ index }
											className={ s.block }
										>
											<TimeInput
												variant={ s.timeInputVariant }
												value={ block.start }
												onChange={ ( val ) =>
													updateBlock(
														key,
														index,
														'start',
														val
													)
												}
												ariaLabel={ __(
													'Start time',
													'nemtly-booking'
												) }
											/>
											<span className={ s.dash }>–</span>
											<TimeInput
												variant={ s.timeInputVariant }
												value={ block.end }
												onChange={ ( val ) =>
													updateBlock(
														key,
														index,
														'end',
														val
													)
												}
												ariaLabel={ __(
													'End time',
													'nemtly-booking'
												) }
											/>

											{ /* Remove block */ }
											{ blocks.length > 1 && (
												<button
													type="button"
													className={ `${ s.iconBtn } ${ s.removeBtn }` }
													onClick={ () =>
														removeBlock(
															key,
															index
														)
													}
													title={ __(
														'Remove time block',
														'nemtly-booking'
													) }
													aria-label={ __(
														'Remove time block',
														'nemtly-booking'
													) }
												>
													<svg
														className="nb-w-4 nb-h-4"
														fill="none"
														stroke="currentColor"
														viewBox="0 0 24 24"
													>
														<path
															strokeLinecap="round"
															strokeLinejoin="round"
															strokeWidth="2"
															d="M6 18L18 6M6 6l12 12"
														/>
													</svg>
												</button>
											) }

											{ /* Add block (only on last row) */ }
											{ index === blocks.length - 1 && (
												<button
													type="button"
													className={ `${ s.iconBtn } ${ s.addBtn }` }
													onClick={ () =>
														addBlock( key )
													}
													title={ __(
														'Add time block',
														'nemtly-booking'
													) }
													aria-label={ __(
														'Add time block',
														'nemtly-booking'
													) }
												>
													<svg
														className="nb-w-4 nb-h-4"
														fill="none"
														stroke="currentColor"
														viewBox="0 0 24 24"
													>
														<path
															strokeLinecap="round"
															strokeLinejoin="round"
															strokeWidth="2"
															d="M12 4v16m8-8H4"
														/>
													</svg>
												</button>
											) }
										</div>
									) ) }
								</div>
							) : (
								<p className={ s.offLabel }>
									{ __( 'Day off', 'nemtly-booking' ) }
								</p>
							) }
						</div>
					</div>
				);
			} ) }
		</div>
	);
};

export default AvailabilityEditor;
